import React, { useCallback, useEffect, useState } from 'react';

import { DownloadFormat } from '../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';

const FORMAT_OPTIONS = [
  { label: 'Excel', value: DownloadFormat.EXCEL },
  { label: 'Docx', value: DownloadFormat.DOCX },
  { label: 'CSV', value: DownloadFormat.CSV },
];

interface Props {
  columnOptions: string[];
  downloadTitle: string;
  loadingDownload: boolean;
  totalCount?: number;
  handleDownload: ({
    title,
    columns,
    downloadFormat,
  }: {
    title: string;
    columns: string[];
    downloadFormat: DownloadFormat;
  }) => void;
}

export const DownloadTableModal = ({
  columnOptions,
  downloadTitle,
  loadingDownload,
  totalCount,
  handleDownload,
}: Props) => {
  const [title, setTitle] = useState(downloadTitle);
  const [columns, setColumns] = useState<string[]>(columnOptions);
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>(FORMAT_OPTIONS[0].value);

  useEffect(() => {
    setTitle(downloadTitle);
  }, [downloadTitle]);

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleDownloadClick = () => {
    const columnValues = columns.map((column) => column).filter((v) => v !== undefined) as string[];

    if (!downloadFormat) return;
    handleDownload({
      title,
      columns: columnValues,
      downloadFormat: downloadFormat,
    });
  };

  return (
    <>
      {loadingDownload ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl font-medium text-gray-700">Preparing your download...</div>

          <Icon className="lucide--loader-circle fa-spin text-brandSecondary text-4xl" />
        </div>
      ) : (
        <div className="px-8 py-6">
          {totalCount !== undefined && (
            <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-700">
              This download will include <span className="font-semibold">{totalCount}</span>{' '}
              {totalCount === 1 ? 'item' : 'items'}
            </div>
          )}
          <div className="mb-6">
            <div className="mb-4 flex flex-col gap-2">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                File Name
              </Label>
              <Input
                id="title"
                onChange={handleChangeTitle}
                value={title}
                placeholder="Enter a title for your file"
                className="w-full"
              />
            </div>
            <div className="mb-6 flex flex-col gap-2">
              <Label className="text-sm font-medium text-gray-700">Select Columns</Label>

              <MultiSelect
                values={columns}
                onValuesChange={(values) => {
                  setColumns(values);
                }}
              >
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="Select themes..." />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  <MultiSelectGroup>
                    {columnOptions.map((column) => (
                      <MultiSelectItem key={column} value={column}>
                        {column}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectGroup>
                </MultiSelectContent>
              </MultiSelect>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-sm font-medium text-gray-700">Format</Label>

              <Select
                value={downloadFormat}
                onValueChange={(newValue) => setDownloadFormat(newValue as DownloadFormat)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="my-6 border-t border-gray-200"></div>

            <Button className="w-full items-center justify-center" onClick={handleDownloadClick}>
              Download
              <Icon className="lucide--download h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
